import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Alert
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  AttachFile as AttachFileIcon
} from "@mui/icons-material";
import { colors, fonts, spacing, borderRadius } from "../../../constants";
import { formatFileSize } from "../../../utils/fileUtils";
import { ACCEPTED_FILE_INPUT_TYPES } from "../../../constants/raiseQueryForm";

/**
 * ResponseForm Component
 * Response input form with file upload and drag & drop
 *
 * @param {string} responseMessage - Response message value
 * @param {Array} attachments - Array of attached files
 * @param {boolean} isDragging - Whether files are being dragged
 * @param {boolean} canRespond - Whether user can respond
 * @param {boolean} isSubmittingResponse - Whether response is being submitted
 * @param {boolean} isSavingDraft - Whether draft is being saved
 * @param {string} responseError - Error message
 * @param {boolean} isSubmittedStatus - Whether query is in submitted status
 * @param {boolean} isClosed - Whether query is closed
 * @param {boolean} isEscalatedViewOnly - Whether user has view-only access
 * @param {boolean} isThreadDisabledForOriginator - Whether thread is disabled for originator
 * @param {boolean} hasRespondAccess - Whether user has respond access
 * @param {function} onResponseChange - Response message change handler
 * @param {function} onFileSelect - File select handler
 * @param {function} onFileRemove - File remove handler
 * @param {object} onDragHandlers - Drag event handlers { onDragOver, onDragLeave, onDrop }
 * @param {function} onSaveDraft - Save draft handler
 * @param {function} onSubmit - Submit handler
 * @param {object} fileInputRef - File input ref
 */
const ResponseForm = ({
  responseMessage,
  attachments,
  isDragging,
  canRespond,
  isSubmittingResponse,
  isSavingDraft,
  responseError,
  isSubmittedStatus,
  isClosed,
  isEscalatedViewOnly,
  isThreadDisabledForOriginator,
  hasRespondAccess,
  onResponseChange,
  onFileSelect,
  onFileRemove,
  onDragHandlers,
  onSaveDraft,
  onSubmit,
  fileInputRef
}) => {
  const handleFileInputClick = () => {
    fileInputRef?.current?.click();
  };

  const isResponseSectionDisabled =
    !canRespond ||
    isSubmittingResponse ||
    isSavingDraft ||
    isClosed ||
    isSubmittedStatus ||
    isEscalatedViewOnly ||
    isThreadDisabledForOriginator;

  let responseSectionDisabledMessage = "";
  if (isClosed) {
    responseSectionDisabledMessage =
      "This query is closed. Only reopening is allowed.";
  } else if (isSubmittedStatus) {
    responseSectionDisabledMessage =
      "Query is submitted. Only the assignor can reopen it for further responses.";
  } else if (isEscalatedViewOnly) {
    responseSectionDisabledMessage =
      "This query is escalated and view-only for your role. You cannot respond from this view.";
  } else if (isThreadDisabledForOriginator) {
    responseSectionDisabledMessage =
      "Conversation thread is disabled until the assignee posts the first response.";
  } else if (!hasRespondAccess) {
    responseSectionDisabledMessage =
      "You do not have permission to respond to this query.";
  }

  const [accessRights, setAccessRights] = useState({});

  useEffect(() => {
    try {
      const rights = JSON.parse(localStorage.getItem("AccessRights")) || {};
      setAccessRights(rights);
    } catch {
      setAccessRights({});
    }
  }, []);
  const shouldShowSave = accessRights["1042"] === "Y";
  const shouldShowSubmit = accessRights["1043"] === "Y";

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: spacing.sm
      }}
    >
      {responseError && (
        <Alert severity="error" sx={{ fontSize: fonts.fontSizeSm }}>
          {responseError}
        </Alert>
      )}

      {isResponseSectionDisabled && responseSectionDisabledMessage && (
        <Alert severity="info" sx={{ fontSize: fonts.fontSizeSm }}>
          {responseSectionDisabledMessage}
        </Alert>
      )}

      <TextField
        fullWidth
        multiline
        rows={4}
        value={responseMessage}
        onChange={onResponseChange}
        placeholder="Type your response here..."
        disabled={isResponseSectionDisabled}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: borderRadius.input,
            fontSize: fonts.fontSizeMd,
            "&:hover fieldset": {
              borderColor: colors.primary
            },
            "&.Mui-focused fieldset": {
              borderColor: colors.primary
            }
          }
        }}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: spacing.xs
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPTED_FILE_INPUT_TYPES}
          style={{ display: "none" }}
          onChange={onFileSelect}
          disabled={isResponseSectionDisabled}
        />

        <Box
          onDragOver={onDragHandlers?.onDragOver}
          onDragLeave={onDragHandlers?.onDragLeave}
          onDrop={onDragHandlers?.onDrop}
          onClick={handleFileInputClick}
          sx={{
            p: spacing.md,
            borderRadius: borderRadius.input,
            border: `2px dashed ${isDragging ? colors.primaryDark : colors.borderDefault}`,
            backgroundColor: isDragging
              ? colors.primaryTint
              : colors.backgroundWhite,
            cursor: !isResponseSectionDisabled ? "pointer" : "not-allowed",
            transition: "all 0.2s ease-in-out",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: spacing.xs,
            opacity: !isResponseSectionDisabled ? 1 : 0.6,
            "&:hover": !isResponseSectionDisabled
              ? {
                  borderColor: colors.primaryDark,
                  background: colors.primarySoft
                }
              : {}
          }}
        >
          <CloudUploadIcon
            sx={{
              fontSize: "2rem",
              color: isDragging ? colors.primaryDark : "#94a3b8",
              transition: "color 0.2s ease-in-out"
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: fonts.weightSemiBold,
              color: colors.textPrimary,
              fontSize: fonts.fontSizeSm,
              textAlign: "center"
            }}
          >
            Drag & drop files here or{" "}
            <Box
              component="span"
              sx={{ color: colors.primary, textDecoration: "underline" }}
            >
              browse
            </Box>
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: colors.textSecondary,
              fontSize: fonts.fontSizeXs,
              textAlign: "center"
            }}
          >
            Optional: Attach supporting documents (multiple files allowed)
          </Typography>
        </Box>

        {/* Attached Files List */}
        {attachments.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: spacing.xs,
              mt: spacing.xs
            }}
          >
            {attachments.map((file, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: spacing.xs,
                  borderRadius: borderRadius.md,
                  border: `1px solid ${colors.primaryLight}`,
                  backgroundColor: colors.backgroundWhite,
                  display: "flex",
                  alignItems: "center",
                  gap: spacing.sm,
                  justifyContent: "space-between"
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacing.xs,
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  <AttachFileIcon
                    sx={{
                      color: colors.primaryDark,
                      fontSize: "1.25rem",
                      flexShrink: 0
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: fonts.weightMedium,
                        color: colors.textPrimaryDark,
                        fontSize: fonts.fontSizeSm,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {file.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.textSecondary,
                        fontSize: fonts.fontSizeXs
                      }}
                    >
                      {formatFileSize(file.size)}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onFileRemove(index);
                  }}
                  disabled={isSubmittingResponse || isSavingDraft}
                  sx={{
                    textTransform: "none",
                    fontSize: fonts.fontSizeXs,
                    minWidth: "auto",
                    padding: `${spacing.xs} ${spacing.sm}`
                  }}
                >
                  Remove
                </Button>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: spacing.xs
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: spacing.xs,
            flex: 1
          }}
        >
          {shouldShowSave && (
            <Button
              onClick={onSaveDraft}
              variant="outlined"
              disabled={
                !canRespond ||
                isSavingDraft ||
                isSubmittingResponse ||
                !responseMessage.trim()
              }
              sx={{
                textTransform: "none",
                borderRadius: borderRadius.button,
                padding: `${spacing.xs} ${spacing.md}`,
                minWidth: "100px",
                fontWeight: fonts.weightSemiBold,
                fontSize: fonts.fontSizeSm,
                borderColor: colors.info,
                color: colors.info,
                "&:hover": {
                  borderColor: colors.info,
                  backgroundColor: `${colors.info}10`
                },
                "&:disabled": {
                  borderColor: colors.borderLight,
                  color: colors.textSecondary
                }
              }}
            >
              {isSavingDraft ? "Saving..." : "Save"}
            </Button>
          )}
          {shouldShowSubmit && (
            <Button
              type="submit"
              variant="contained"
              disabled={!canRespond || isSubmittingResponse || isSavingDraft}
              sx={{
                textTransform: "none",
                borderRadius: borderRadius.button,
                padding: `${spacing.xs} ${spacing.md}`,
                minWidth: "120px",
                fontWeight: fonts.weightSemiBold,
                fontSize: fonts.fontSizeSm,
                background: colors.primaryGradient,
                color: `${colors.textWhite} !important`,
                boxShadow: `0 4px 12px ${colors.primaryShadow}`,
                "&:hover": {
                  background: colors.primaryGradientHover,
                  boxShadow: `0 6px 16px ${colors.primaryShadowHover}`
                },
                "&.Mui-disabled": {
                  background: colors.borderLight,
                  color: `${colors.textSecondary} !important`,
                  boxShadow: "none",
                  opacity: 1
                }
              }}
            >
              {isSubmittingResponse ? "Submitting..." : "Submit"}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ResponseForm;
